import React, { useState } from 'react';
import { Box, Button, IconButton, Typography, Stack } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import { showToastMessage } from '../../utils/AppUtils';
import { pdfOnly } from './data';
import { useThemeContext } from '../../hook/useThemeManager';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { theme } from '../../constants/theme';
import { DriveFolderUpload } from '@mui/icons-material';

type UploadComponentProps = {
    uploadFormats?: string[]; // Allowed file formats (e.g., ['image/png', 'application/pdf'])
    onChange: (filePath: File | null) => void; // Callback for returning the file path
    initialFile?: File;
};

const UploadComponent: React.FC<UploadComponentProps> = ({ uploadFormats = pdfOnly, onChange, initialFile = null }) => {
    const [selectedFile, setSelectedFile] = useState<File | null>(initialFile);
    const { themeStyle } = useThemeContext();

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            if (uploadFormats.includes(file.type)) {
                setSelectedFile(file);
                // const filePath = URL.createObjectURL(file); // Simulate upload path
                onChange(file);
            } else {
                showToastMessage(`Unsupported file format. Allowed formats: ${uploadFormats.join(', ')}`, 'info');
            }
        }
    };

    const handleCancelUpload = () => {
        setSelectedFile(null);
        onChange(null); // Return null to indicate file removal
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                p: 2,
                border: '1px solid grey',
                borderRadius: 1,
            }}>
            {!selectedFile ? (
                <>
                    <Button
                        variant="text"
                        component="label"
                        sx={{
                            color: theme[themeStyle].tag_white,
                        }}
                        startIcon={<DriveFolderUpload sx={{ fontSize: 24, color: theme[themeStyle].tag_white }} />}>
                        Upload File
                        <input type="file" hidden accept={uploadFormats.join(',')} onChange={handleFileChange} />
                    </Button>
                    <Typography
                        variant="body2"
                        sx={{ color: theme[themeStyle].tag_white, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                        Allowed formats: {uploadFormats.join(', ')}
                    </Typography>
                </>
            ) : (
                <Stack direction="row" alignItems="center" spacing={2}>
                    <Typography
                        variant="body2"
                        sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, color: theme[themeStyle].tag_white }}>
                        {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                    </Typography>
                    <IconButton color="error" onClick={handleCancelUpload} aria-label="Cancel Upload">
                        <CancelIcon />
                    </IconButton>
                </Stack>
            )}
        </Box>
    );
};

export default UploadComponent;
